'use client';

import { c as _c } from "react/compiler-runtime";
import './help-me-choose.scss';
import { parseAsInteger, parseAsString, useQueryState, useQueryStates } from 'nuqs';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StepperTabbed } from '@sola/ark-core/molecules/stepper-tabbed/stepper-tabbed.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { childSearch, getChild, getRandomChildren } from '@sola/proxy-sdk/children.ts';
import { ChildAvailabilityItem, ChildAvailabilitySearchResults, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import { HelpMeChooseResultSkeleton } from '../../organisms/help-me-choose/result/result-skeleton.tsx';
import { HelpMeChooseResult } from '../../organisms/help-me-choose/result/result.tsx';
import { HelpMeChooseAge } from './answers/help-me-choose-age.tsx';
import { HelpMeChooseCause } from './answers/help-me-choose-cause.tsx';
import { HelpMeChooseGender } from './answers/help-me-choose-gender.tsx';
import { HelpMeChooseRegion } from './answers/help-me-choose-region.tsx';
import { HelpMeChooseFooter } from './help-me-choose-footer.tsx';
import { HelpMeChoosePage } from './help-me-choose-page.tsx';
import { filteredCountries } from './utils/filtered-coutries.tsx';
export const HelpMeChooseInner: React.FC = () => {
  const {
    t
  } = useTranslation('help-me');
  const [step, setStep] = useQueryState('step', parseAsInteger.withDefault(0));
  const [causes, setCauses] = useQueryState('filters', parseAsString);
  const [anyCause, setAnyCause] = useState<boolean>(false);
  const [gender, setGender] = useQueryState('gender', parseAsString);
  const [ages, setAges] = useQueryStates({
    ageMin: parseAsInteger,
    ageMax: parseAsInteger
  });
  const [location, setLocation] = useQueryState('countries', parseAsString);
  const [children, setChildren] = useState<ChildAvailabilityItem[]>([]);
  const [childDetails, setChildDetails] = useState<ChildDetails>();
  const [skipChild, setSkipChild] = useState<number>(0);
  const [noMatch, setNoMatch] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const lengthMinusOne = 4;
  const lengthMinusTwo = 3;
  const defaultAmountToTake = 120;
  const skip = () => {
    switch (Number(step)) {
      case 0:
        return !causes && !anyCause;
      case 1:
        return !gender;
      case 2:
        return !(ages.ageMin || ages.ageMax);
      case 3:
        return !location;
      case 4:
        return false;
      default:
        return false;
    }
  };
  const backwardsLabel = () => {
    if (step !== lengthMinusOne) {
      return t('help-me:pagination.back');
    }
    if (step === lengthMinusOne) {
      return t('help-me:pagination.startOver');
    }
  };
  const backwardsClick = () => {
    if (step === lengthMinusOne) {
      setAnyCause(false);
      setCauses(null);
      setGender(null);
      setAges(null);
      setLocation(null);
      setSkipChild(0);
      setStep(0);
    }
    if (step !== lengthMinusOne) {
      setStep(Math.max(step - 1, 0));
    }
  };
  const forwardLabel = () => {
    if (step < lengthMinusTwo) {
      return skip() ? t('help-me:pagination.skip') : t('help-me:pagination.next');
    }
    if (step === lengthMinusTwo) {
      return skip() ? t('help-me:pagination.skipAndView') : t('help-me:pagination.meetMyMatch');
    }
    if (step === lengthMinusOne) {
      return t('help-me:pagination.refreshResults');
    }
  };
  const forwardVariant = () => {
    return skip() ? 'secondary' : 'primary';
  };
  const forwardIcon = () => {
    if (!skip()) {
      return <ArkIcon iconName={step < lengthMinusOne ? 'arrow-narrow-right' : 'refresh-ccw-05'} />;
    }
    return undefined;
  };
  const forwardClick = () => {
    if (step === lengthMinusOne) {
      setSkipChild(skipChild + 1);
    }
    if (step !== lengthMinusOne) {
      setStep(Math.min(step + 1, lengthMinusOne));
    }
  };
  const hasCriteria = () => {
    if (!gender && !ages.ageMin && !ages.ageMax && !causes && !filteredCountries(causes, location)) {
      return false;
    }
    return true;
  };
  const replaceDetails = async (newKid: ChildAvailabilityItem) => {
    let details;
    try {
      details = await getChild(newKid?.globalId);
      setChildDetails(details);
    } catch {
      console.error('Child missing details');
    }
  };
  const baseSearchParams = {
    take: defaultAmountToTake,
    gender: gender as ChildGenderEnum,
    ageMin: ages.ageMin || 1,
    ageMax: ages.ageMax || 22,
    skip: 0,
    countries: location ? location.split(',') : []
  };
  const fetchChildren = async () => {
    setNoMatch(false);
    setIsLoading(true);
    let childResults: ChildAvailabilitySearchResults | undefined;
    try {
      if (hasCriteria()) {
        childResults = await childSearch({
          ...baseSearchParams,
          disability: causes === 'hasDisabilities' ? true : undefined,
          isInHivAffectedArea: causes === 'affectedHiv' ? true : undefined,
          countries: filteredCountries(causes, location) ? filteredCountries(causes, location)?.split(',') : []
        });
      }
      if (hasCriteria() && childResults?.children?.length === 0) {
        childResults = await childSearch(baseSearchParams);
        setNoMatch(true);
      }
      if (!hasCriteria()) {
        childResults = await getRandomChildren(defaultAmountToTake);
      }
      if (childResults?.children[0]) {
        await replaceDetails(childResults?.children[0]);
      }
    } catch (error) {
      console.error('Error fetching children:', {
        error
      });
      setNoMatch(true);
      childResults = await getRandomChildren(defaultAmountToTake);
    } finally {
      setChildren(() => [...(childResults?.children || [])]);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    setSkipChild(0);
    fetchChildren();
  }, [causes, anyCause, gender, ages, ages.ageMax, ages.ageMin, location]);
  useEffect(() => {
    const fetchDetails = async () => {
      if (skipChild !== 0) {
        setIsLoading(true);
        const index = skipChild < children.length ? skipChild : 0;
        const newKid_0 = children[index];
        await replaceDetails(newKid_0);
        setIsLoading(false);
        if (index === 0) {
          setSkipChild(0);
        }
      }
    };
    fetchDetails();
  }, [skipChild]);
  return <section>
      <div className="HelpMeChoose HideBreadcrumbs HideFooter">
        <div>
          <StepperTabbed items={[{
          title: t('help-me:pagination.cause'),
          body: <HelpMeChoosePage header={t('help-me:questions.how')} description={t('help-me:questions.selectHow')} answers={<HelpMeChooseCause setAnyCause={setAnyCause} />} />
        }, {
          title: t('help-me:pagination.gender'),
          body: <HelpMeChoosePage header={t('help-me:questions.gender')} description={t('help-me:questions.selectGender')} answers={<HelpMeChooseGender />} />
        }, {
          title: t('help-me:pagination.age'),
          body: <HelpMeChoosePage header={t('help-me:questions.age')} description={t('help-me:questions.selectAge')} answers={<HelpMeChooseAge />} />
        }, {
          title: t('help-me:pagination.location'),
          body: <HelpMeChoosePage header={t('help-me:questions.region')} description={t('help-me:questions.selectRegion')} answers={<HelpMeChooseRegion />} />
        }, {
          title: t('help-me:pagination.yourMatch'),
          body: <div className="HelpMeChoose__page">
                    {!isLoading && children && children[skipChild] && childDetails && <>
                          <div className="mb-comfy-lg">
                            <h1 className="heading-sm heading-neutral-amplified">
                              {noMatch ? t('help-me:result.headingB') : t('help-me:result.heading')}
                            </h1>
                            <p className="label-moderate label-lg mt-snug-lg">
                              {noMatch ? children[skipChild] && t('help-me:result.noMatch') : children[skipChild] && t('help-me:result.subheading', {
                    childName: children[skipChild].name.preferred,
                    interpolation: {
                      escapeValue: false
                    }
                  })}
                            </p>
                          </div>
                          <HelpMeChooseResult childDetails={childDetails} childAvailability={children[skipChild]} />
                        </>}
                    {(isLoading || !children || children?.length === 0 || !childDetails) && <HelpMeChooseResultSkeleton />}
                  </div>
        }]} />
        </div>
        <HelpMeChooseFooter backwardsLabel={backwardsLabel()} backwardsClick={backwardsClick} forwardLabel={forwardLabel()} forwardVariant={forwardVariant()} forwardIcon={forwardIcon()} forwardClick={forwardClick} />
      </div>
    </section>;
};
export const HelpMeChoose: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense><HelpMeChooseInner /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};